import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Search, MapPin, Store, CalendarPlus, Sparkles, Tag } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";

const heroQuickLinks = [
  { label: "Add Business", to: "/add-business", icon: Store },
  { label: "Add Event", to: "/add-event", icon: CalendarPlus },
  { label: "Experiences", to: "/experiences", icon: Sparkles },
  { label: "Deals", to: "/deals", icon: Tag },
];

const SKYLINE =
  "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1780283660633-vb3q7h.png";

const DEFAULT_LOCATIONS = ["All Locations", "Frisco", "Plano", "Irving", "Lewisville", "McKinney", "Dallas", "Aubrey", "Other"];

// Only the top 3 most-visited categories are shown on mobile-compact hero
// banners. There's no page-view/analytics tracking wired up in this project,
// so "most visited" is approximated from the categories with the most listed
// businesses (Restaurants, Shopping, Party Venues have by far the largest
// counts in src/data — a reasonable stand-in until real analytics exist).
const trendingItems = [
  "Restaurants",
  "Shopping",
  "Party Venues",
];

const trendingRoutes: Record<string, string> = {
  "Restaurants": "/restaurants",
  "Shopping": "/shopping",
  "Party Venues": "/party-venues",
  "Grocery": "/grocery",
  "Concerts/Events": "/concerts",
  "Temples/Spiritual Services": "/temples",
};

interface CategoryHeroProps {
  title?: string;     // Page title for semantic <h1>
  subtitle?: string;  // Optional subtitle (rendered below title)
  placeholder?: string;
  backgroundImage?: string | string[]; // override the default skyline background or provide an array of images for a collage
  // Optional controlled search state (used by Events to drive filtering).
  query?: string;
  setQuery?: (v: string) => void;
  location?: string;
  setLocation?: (v: string) => void;
  locations?: string[];
  hideLocation?: boolean;
}

// Reusable category-page hero: branded background image + a search bar with a
// location dropdown. Works uncontrolled (presentational) or controlled.
export default function CategoryHero({
  title,
  placeholder = "Search…",
  backgroundImage,
  query,
  setQuery,
  location,
  setLocation,
  locations = DEFAULT_LOCATIONS,
  hideLocation,
}: CategoryHeroProps) {
  const navigate = useNavigate();
  const [iq, setIq] = useState("");
  const [il, setIl] = useState(locations[0] || "All Locations");
  const q = query !== undefined ? query : iq;
  const sq = setQuery || setIq;
  const loc = location !== undefined ? location : il;
  const sl = setLocation || setIl;

  const [open, setOpen] = useState(false);
  const [dropdownSearch, setDropdownSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const dropdownInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    function h(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setDropdownSearch("");
      }
    }
    if (open) {
      document.addEventListener("mousedown", h);
      setTimeout(() => dropdownInputRef.current?.focus(), 50);
      return () => document.removeEventListener("mousedown", h);
    }
  }, [open]);

  const filteredLocations = locations.filter((loc) =>
    loc.toLowerCase().includes(dropdownSearch.toLowerCase())
  );

  // Submitting from ANY category-page hero triggers a real global search —
  // it takes the visitor to the homepage's site-wide search results, which
  // scan every category (restaurants, events, shopping, temples, cricket,
  // etc.), not just the local list on the page they started from.
  const runGlobalSearch = () => {
    const trimmed = q.trim();
    if (trimmed.length === 0) return;
    navigate(`/?q=${encodeURIComponent(trimmed)}`);
  };

  const resolvedBackgroundImage = Array.isArray(backgroundImage)
    ? backgroundImage[0]
    : backgroundImage;

  return (
    <section className="relative w-full" style={{ minHeight: "160px" }}>
      {/* Background image + overlay + bottom curve */}
      <div className="absolute inset-0 overflow-hidden">
        <img
          src={resolvedBackgroundImage || SKYLINE}
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-[center_20%]"
        />
        <div className="absolute inset-0 bg-black/45" />
        <svg viewBox="0 0 1440 24" preserveAspectRatio="none" style={{ position: "absolute", left: 0, bottom: "-1px", width: "100%", height: "18px", display: "block" }}>
          <path d="M0,0 Q720,24 1440,0 L1440,24 L0,24 Z" fill="#ffffff" />
        </svg>
      </div>

      {/* Content — semantic h1 + search bar + quick links */}
      <div className="relative max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 py-3 sm:py-4 flex flex-col items-center justify-center" style={{ zIndex: 50, minHeight: "160px" }}>
        {title && (
          <h1 className="sr-only">{title}</h1>
        )}
        {/* Quick action links */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.2 }}
          className="mt-2 sm:mt-2.5 flex flex-nowrap items-center justify-center gap-1.5 sm:gap-2 w-full max-w-[420px] sm:max-w-[555px]"
        >
          {heroQuickLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="bg-[#f59e0b] hover:bg-[#d97f06] text-white text-[9.5px] xs:text-[10.5px] sm:text-[12px] font-semibold px-1.5 sm:px-3.5 py-1.5 sm:py-2 min-h-[32px] sm:min-h-[36px] rounded-md transition-colors duration-200 shadow-sm flex items-center justify-center text-center leading-tight flex-1 sm:flex-initial whitespace-nowrap"
            >
              {link.label}
            </Link>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
